import React, { useEffect, useState, useRef } from "react";
import styles from "../styles/RunWorkflowModal.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSync,
  faPaperPlane,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { cancelTask, createTask, createWaldiezFileFromJson } from "../api/runner_api";
import { logWorkflowRun, updateWorkflowRunStatus } from "../api/api";
import { useContext } from "react";
import { AuthContext } from "../AuthProvider";

const USER_COLOR = "rgb(96, 96, 255)";
const SYSTEM_COLOR = "#888888";
const AI_COLOR = "rgb(82, 205, 224)";
const IMG_DIR = "/images";
const AVATAR_COLORS = [
  "rgb(163, 82, 224)",
  "rgb(82, 224, 122)",
  "rgb(82, 205, 224)",
  "rgb(255, 149, 0)",
  "rgb(255, 80, 80)",
  "rgb(80, 130, 255)",
  "rgb(255, 204, 0)",
];

const webSocketServer = process.env.REACT_APP_WEBSOCKET_URL;

// Helper to assign consistent color per sender
const senderColorMap = new Map();
function getColorForParticipant(name) {
  if (!senderColorMap.has(name)) {
    const index = senderColorMap.size % AVATAR_COLORS.length;
    senderColorMap.set(name, AVATAR_COLORS[index]);
  }
  return senderColorMap.get(name);
}

// Escape HTML
function escapeHtml(str) {
  if (!str) return "<i>Empty message</i>";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

// Try to parse JSON
function tryParse(data) {
  try {
    if (typeof data === "object" && data !== null && data !== undefined) {
      // already an object?
      return data;
    }
    return JSON.parse(data);
  } catch {
    return null;
  }
}

const RunWorkflowModal = ({ isOpen, onClose, workflowUrl, workflowId }) => {
  const { keycloak, authenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState("pending");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [knownSenders, setKnownSenders] = useState(new Set());
  const [currentUser, setCurrentUser] = useState("You");
  // For custom params modal
  const [showParamsModal, setShowParamsModal] = useState(false);
  const [pendingParamsCallback, setPendingParamsCallback] = useState(null);
  const [requiredKeys, setRequiredKeys] = useState(null); // array of keys or null
  const [replaceMeInputs, setReplaceMeInputs] = useState({});
  const [hiddenInputs, setHiddenInputs] = useState(new Set()); // Track which inputs should be hidden

  const wsRef = useRef(null);
  const chatBoxRef = useRef(null);
  const subprotocol = "tasks-api";

  // Refs to hold the current value of state, avoiding stale closures in callbacks
  const taskIdRef = useRef(taskId);
  useEffect(() => {
    taskIdRef.current = taskId;
  }, [taskId]);

  const useUrl = true;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);


  // Recursively find all keys with value 'REPLACE_ME' in an object/array
  function findReplaceMeFields(obj, path = [], apiType = null) {
    let fields = [];
    if (Array.isArray(obj)) {
      obj.forEach((item, idx) => {
        fields = fields.concat(findReplaceMeFields(item, path.concat(idx), apiType));
      });
    } else if (obj && typeof obj === "object") {
      // Find apiType if present at this level
      let currentApiType = apiType;
      if (obj.apiType) {
        currentApiType = obj.apiType;
      }
      for (const key of Object.keys(obj)) {
        if (obj[key] === "REPLACE_ME") {
          // If under 'aws' and apiType is not 'bedrock', skip
          if (path.includes("aws") && currentApiType !== "bedrock") {
            continue;
          }
          fields.push({ path: path.concat(key), key });
        } else {
          // If entering 'aws', pass currentApiType
          fields = fields.concat(findReplaceMeFields(obj[key], path.concat(key), currentApiType));
        }
      }
    }
    return fields;
  }

  // Helper to set value at a path in an object (immutably)
  function setValueAtPath(obj, path, value) {
    if (path.length === 0) return value;
    const [head, ...rest] = path;
    if (Array.isArray(obj)) {
      const newArr = obj.slice();
      newArr[head] = setValueAtPath(obj[head], rest, value);
      return newArr;
    } else if (typeof obj === "object" && obj !== null) {
      return { ...obj, [head]: setValueAtPath(obj[head], rest, value) };
    }
    return obj;
  }

  // Helper to prompt user for all REPLACE_ME values at once
function getAllReplaceMeValues(fields, jsonObj) {
  // Map apiKey to provider-specific key for display
  const displayKeys = fields.map(field => {
    if (field.key === "apiKey") {
      // Find apiType at the same level
      let apiType = null;
      let parent = jsonObj;
      for (let i = 0; i < field.path.length - 1; i++) {
        parent = parent[field.path[i]];
      }
      if (parent && parent.apiType) {
        apiType = parent.apiType;
      }
      let keyMap = {
        openai: "OPENAI_API_KEY",
        azure: "AZURE_API_KEY",
        deepseek: "DEEPSEEK_API_KEY",
        google: "GOOGLE_GEMINI_API_KEY",
        anthropic: "ANTHROPIC_API_KEY",
        mistral: "MISTRAL_API_KEY",
        groq: "GROQ_API_KEY",
        together: "TOGETHER_API_KEY",
        nim: "NIM_API_KEY",
        cohere: "COHERE_API_KEY",
        other: "OPENAI_API_KEY",
      };
      let mappedKey = keyMap[(apiType || "other").toLowerCase()] || "OPENAI_API_KEY";
      return { ...field, displayKey: mappedKey };
    }
    return { ...field, displayKey: field.key };
  });
  return new Promise((resolve, reject) => {
    setRequiredKeys(displayKeys); // store for modal rendering
    setReplaceMeInputs(Object.fromEntries(displayKeys.map(f => [f.displayKey, ""])));
    setHiddenInputs(new Set(displayKeys.map(f => f.displayKey))); // Hide all inputs by default
    setShowParamsModal(true);
    setPendingParamsCallback(() => (vals) => {
      setShowParamsModal(false);
      setPendingParamsCallback(null);
      setReplaceMeInputs({});
      setHiddenInputs(new Set()); // Reset hidden inputs
      // Check if this is a cancellation
      if (vals && vals.__CANCELLED__) {
        resolve({ __CANCELLED__: true });
        return;
      }
      // Map back to original keys for resolve
      const result = {};
      for (const field of displayKeys) {
        result[field.path.join('.')] = vals[field.displayKey];
      }
      resolve(result);
    });
  });
}  // Render modal for filling all REPLACE_ME values at once
  const renderParamsModal = () => {
    if (!showParamsModal) return null;
    const keys = Array.isArray(requiredKeys) ? requiredKeys : (requiredKeys ? [requiredKeys] : []);
    const allFilled = keys.every(f => replaceMeInputs[f.displayKey] && replaceMeInputs[f.displayKey].trim());
    return (
      <div className={styles.paramsModalOverlay}>
        <div className={styles.paramsModalBox}>
          <div className={styles.paramsModalHeader}>
            <h2 className={styles.paramsModalTitle}>Values Required</h2>
          </div>
          <div className={styles.paramsModalBody}>
            <div className={styles.paramsModalText}>
              Please enter values for the following fields:<br />
              <span className={styles.paramsModalHelper}>These values will only be used for this run and never stored.</span>
            </div>
            <form className={styles.paramsForm}
              onSubmit={e => {
                e.preventDefault();
                if (pendingParamsCallback && allFilled) pendingParamsCallback(replaceMeInputs);
              }}
            >
              {keys.map((f, i) => {
                const isHidden = hiddenInputs.has(f.displayKey);
                return (
                  <div key={f.displayKey} className={styles.paramsInputField}>
                    <label className={styles.paramsInputLabel} htmlFor={`replaceMeInput_${f.displayKey}`}>{f.displayKey}:</label>
                    <div className={styles.paramsInputContainer}>
                      <input
                        id={`replaceMeInput_${f.displayKey}`}
                        type={isHidden ? "password" : "text"}
                        className={styles.paramsInput}
                        placeholder={`Enter value for ${f.displayKey}...`}
                        value={replaceMeInputs[f.displayKey] || ""}
                        onChange={e => setReplaceMeInputs(inputs => ({ ...inputs, [f.displayKey]: e.target.value }))}
                        autoFocus={i === 0}
                      />
                      <button
                        type="button"
                        className={styles.paramsToggleButton}
                        onClick={() => {
                          setHiddenInputs(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(f.displayKey)) {
                              newSet.delete(f.displayKey);
                            } else {
                              newSet.add(f.displayKey);
                            }
                            return newSet;
                          });
                        }}
                        title={isHidden ? "Show value" : "Hide value"}
                      >
                        <FontAwesomeIcon icon={isHidden ? faEye : faEyeSlash} />
                      </button>
                    </div>
                  </div>
                );
              })}
              <div className={styles.paramsModalActions}>
                <button
                  className={styles.paramsSubmitButton}
                  type="submit"
                  disabled={!allFilled}
                >
                  Submit
                </button>
                <button
                  className={styles.paramsCancelButton}
                  type="button"
                  onClick={() => {
                    setShowParamsModal(false);
                    setPendingParamsCallback(null);
                    setReplaceMeInputs({});
                    setHiddenInputs(new Set()); // Reset hidden inputs
                    // Resolve with special cancellation flag
                    if (pendingParamsCallback) pendingParamsCallback({ __CANCELLED__: true });
                    if (typeof onClose === "function") onClose();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens
      setLoading(true);
      setTaskId(null);
      setTaskStatus("Initializing workflow runner...");
      setError(null);
      setWaitingForInput(false);
      setKnownSenders(new Set());
      setCurrentUser("You");

      const runTaskWithApiKeyCheck = async () => {
        if (!authenticated || !keycloak?.token) {
          setError("You must be logged in to run a workflow.");
          setTaskStatus("Authentication required");
          setLoading(false);
          return;
        }
        try {
          const tokn = keycloak.token;
          setTaskStatus("Authentication successful");

          // 1. Fetch the .waldiez file as JSON
          const fileResponse = await fetch(`${workflowUrl}?count=false`);
          if (!fileResponse.ok) {
            throw new Error(`Failed to fetch file from URL: ${fileResponse.status}`);
          }
          let json = await fileResponse.json();

          // 2. Find all fields with value 'REPLACE_ME' and prompt user for all at once
          // Find top-level apiType if present
          let topApiType = null;
          if (json && typeof json === "object" && json.apiType) {
            topApiType = json.apiType;
          }
          const fieldsToFill = findReplaceMeFields(json, [], topApiType);
          let paramsDict = {};
          if (fieldsToFill.length > 0) {
            try {
              const userValues = await getAllReplaceMeValues(fieldsToFill, json);
              // Check if user cancelled
              if (userValues && userValues.__CANCELLED__) {
                setError("Workflow execution cancelled by user.");
                setTaskStatus("Cancelled");
                setLoading(false);
                return;
              }
              if (!userValues || Object.keys(userValues).length !== fieldsToFill.length) {
                setError("All required values must be filled to run this workflow.");
                setTaskStatus("Required values missing");
                setLoading(false);
                return;
              }
              // Replace REPLACE_ME in JSON with user values
              for (const field of fieldsToFill) {
                const pathKey = field.path.join('.');
                const value = userValues[pathKey];
                if (value !== undefined) {
                  json = setValueAtPath(json, field.path, value);
                }
              }
              // Build params dictionary with apiKey mapping
              for (const field of fieldsToFill) {
                const pathKey = field.path.join('.');
                if (field.key === "apiKey") {
                  // Find apiType at the same level
                  let apiType = null;
                  let parent = json;
                  for (let i = 0; i < field.path.length - 1; i++) {
                    parent = parent[field.path[i]];
                  }
                  if (parent && parent.apiType) {
                    apiType = parent.apiType;
                  }
                  let keyMap = {
                    openai: "OPENAI_API_KEY",
                    azure: "AZURE_API_KEY",
                    deepseek: "DEEPSEEK_API_KEY",
                    google: "GOOGLE_GEMINI_API_KEY",
                    anthropic: "ANTHROPIC_API_KEY",
                    mistral: "MISTRAL_API_KEY",
                    groq: "GROQ_API_KEY",
                    together: "TOGETHER_API_KEY",
                    nim: "NIM_API_KEY",
                    cohere: "COHERE_API_KEY",
                    other: "OPENAI_API_KEY",
                  };
                  let mappedKey = keyMap[(apiType || "other").toLowerCase()] || "OPENAI_API_KEY";
                  paramsDict[mappedKey] = userValues[pathKey];
                } else {
                  paramsDict[field.key] = userValues[pathKey];
                }
              }
            } catch (error) {
              // User cancelled the parameters modal
              setError("Workflow execution cancelled by user.");
              setTaskStatus("Cancelled");
              setLoading(false);
              return;
            }
          }

          if (useUrl) {
            // New behavior: pass URL and params directly
            const task = await createTask(tokn, `${workflowUrl}?count=false`, paramsDict);
            setTaskId(task.id);
            setTaskStatus(`Task created with ID: ${task.id}`);
            await logWorkflowRun(workflowId, task.id, tokn);

            // Now that we have a task ID, establish WebSocket connection
            const wsUrl = `${webSocketServer}${task.id}`;
            connectWebSocket(wsUrl, tokn);

            setLoading(false);
            return;
          }

          // Legacy: create a File from the (possibly modified) JSON
          const fileName = workflowUrl.split("/").pop() || "workflow.waldiez";
          const file = createWaldiezFileFromJson(json, fileName);
          const task = await createTask(tokn, file);
          setTaskId(task.id);
          setTaskStatus(`Task created with ID: ${task.id}`);
          await logWorkflowRun(workflowId, task.id, tokn);

          // Now that we have a task ID, establish WebSocket connection
          const wsUrl = `${webSocketServer}${task.id}`;
          connectWebSocket(wsUrl, tokn);

          setLoading(false);
        } catch (err) {
          if (err.status === 429) {
            setError("You have exceeded your daily usage limit. Please try again tomorrow.");
            setTaskStatus("Rate limit exceeded");
            addMessage({
              role: "system",
              sender: "System",
              content: "You have exceeded your daily usage limit. Please try again tomorrow.",
              type: "error",
            });
            setLoading(false);
            return;
          }
          console.error("Error creating task:", err);
          setError(
            "Failed to create task: " + (err.message || "Unknown error")
          );
          setTaskStatus(
            `Error: ${err.message || "Unknown error creating task"}`
          );

          addMessage({
            role: "system",
            sender: "System",
            content: `Error: ${err.message || "Unknown error creating task"}`,
            type: "error",
          });

          setLoading(false);
        }
      };

      runTaskWithApiKeyCheck();

    } else {
      // Close WebSocket when modal closes
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }

    return () => {
      // Cleanup WebSocket on component unmount
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, workflowUrl]);

  const addMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        ...message,
        timestamp: Date.now(),
      },
    ]);
  };

  const connectWebSocket = (url, token) => {
    try {
      const ws = new WebSocket(url, [subprotocol, token]);

      ws.onopen = () => {
        setTaskStatus("Connected to workflow runner");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "status") {
            // If status is null or undefined, show loading state
            const newStatus = data.status || "Loading... (Note: Workflows with CaptainAgent or DocAgent may take 3-5 minutes to start.)";
            setTaskStatus(newStatus);

            // Add a message for significant status changes
            if (data.status && data.status !== "Loading...") {
              addMessage({
                role: "system",
                sender: "System",
                content: `Status: ${data.status}`,
                type: "status",
              });
            }
          } else if (data.type === "input_request") {
            setWaitingForInput(true);
            setCurrentRequestId(data.request_id);
            setTaskStatus(data.data || "Waiting for input...");
          } else if (data.type === "output") {
            addMessage({
              role: "assistant",
              sender: "Runner",
              content: data.output,
              type: "output",
            });
          } else if (data.type === "error") {
            setError(data.error);
            setTaskStatus(`Error: ${data.error}`);
            addMessage({
              role: "system",
              sender: "System",
              content: `Error: ${data.error}`,
              type: "error",
            });
          } else if (data.type === "result") {
            setTaskStatus("Workflow completed successfully");
            if (taskIdRef.current && keycloak.token) updateWorkflowRunStatus(taskIdRef.current, "success", keycloak.token);
          } else if (data.type === "print") {
            handlePrintMessage(data);
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
          setTaskStatus(`Error parsing message: ${err.message}`);
          addMessage({
            role: "system",
            sender: "System",
            content: `Error parsing message: ${err.message}`,
            type: "error",
          });
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setError("WebSocket connection error. Try refreshing.");

        addMessage({
          role: "system",
          sender: "System",
          content: "Connection error with workflow runner",
          type: "error",
        });
      };

      ws.onclose = (event) => {
        // if (event.code !== 1000 && taskId && token) { // Not a clean close
        //   updateWorkflowRunStatus(taskId, "failed", token);
        // }

        addMessage({
          role: "system",
          sender: "System",
          content: `Connection closed${event.reason ? `: ${event.reason}` : ""
            }`,
          type: "info",
        });
      };

      wsRef.current = ws;
    } catch (err) {
      console.error("Error connecting to WebSocket:", err);
      setError(
        "Failed to connect to task monitor: " + (err.message || "Unknown error")
      );

      addMessage({
        role: "system",
        sender: "System",
        content: `Connection error: ${err.message || "Unknown error"}`,
        type: "error",
      });
    }
  };

  const handlePrintMessage = (msg) => {
    const msgData = msg.data;

    if (
      typeof msgData === "string" &&
      msgData.includes("Copying the results to") &&
      msgData.includes("waldiez_out")
    ) {
      setTaskStatus("Workflow completed successfully");
      if (taskIdRef.current && keycloak.token) updateWorkflowRunStatus(taskIdRef.current, "success", keycloak.token);
      addMessage({
        role: "system",
        sender: "System",
        content: "Workflow completed.",
        type: "system",
      });
      return;
    }

    const parsed = tryParse(msgData);
    if (!parsed) return;

    // Handle nested event structure
    let actualEvent = parsed;
    if (parsed.event && parsed.event.type) {
      actualEvent = parsed.event;
    }

    const innerType = actualEvent.type;
    const content = actualEvent.content || {};

    // Handle visible message types
    if (["text", "tool_call", "function_call", "error", "post_carryover_processing", "group_chat_run_chat", "using_auto_reply", "execute_function", "executed_function", "tool_response", "generate_code_execution_reply", "group_chat_resume", "info", "termination_and_human_reply_no_input", "empty"].includes(innerType)) {
      const sender = content.sender || content.speaker || content.recipient || "Assistant";
      if (sender.includes("user1")) {
        return;
      }

      // Update known senders
      setKnownSenders((prev) => {
        const updated = new Set(prev);
        updated.add(sender);
        return updated;
      });

      // Try to detect if this is a user
      if (innerType === "text" && knownSenders.size <= 1) {
        setCurrentUser(sender);
      }

      if (innerType === "text") {
         let messageContent = content.content;
        if (Array.isArray(messageContent)) {
          messageContent = messageContent.map(item => item.text || '').join('');
        } else if (typeof messageContent === 'object' && messageContent !== null && messageContent.text) {
          messageContent = messageContent.text;
        }
        addMessage({
          type: innerType,
          content: messageContent,
          sender,
          role: sender === currentUser ? "user" : "assistant",
        });
      } else if (innerType === "error") {
        
        const jsonPart = content.error.split(' - ')[1];

        let validJsonString = jsonPart.replace(/'/g, '"').replace(/None/g, 'null');
        const parsedError = tryParse(validJsonString);
        if (parsedError) {
          const errorObj = parsedError.error || parsedError;
          const errorMessage = errorObj.message || 'Unknown error';
          setError(errorMessage);
          setTaskStatus(`Error: ${errorMessage}`);
          addMessage({
            role: "system",
            sender: "System",
              content: `Error: ${errorMessage}`,
              type: "error",
            });
          }
        
        // Close existing WebSocket
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        cancelTask(taskIdRef.current, keycloak.token);
        if (taskIdRef.current && keycloak.token) updateWorkflowRunStatus(taskIdRef.current, "failed", keycloak.token);
      } else if (innerType === "post_carryover_processing") {
        const recipient = content.recipient || "Unknown";
        addMessage({
          type: innerType,
          content: `Processing carryover message to ${recipient}: ${content.message}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "group_chat_run_chat") {
        addMessage({
          type: innerType,
          content: `Next speaker: ${content.speaker || parsed.speaker || "Unknown"}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "using_auto_reply") {
        const recipient = content.recipient || "Unknown";
        addMessage({
          type: innerType,
          content: `Auto-reply triggered for ${sender} → ${recipient}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "execute_function") {
        const recipient = content.recipient || "Unknown";
        const funcName = content.func_name || content.function_name || "Unknown function";
        addMessage({
          type: innerType,
          content: `⚡ Executing: ${funcName} → ${recipient}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "executed_function") {
        const funcName = content.func_name || content.function_name || "Unknown function";
        const success = content.is_exec_success !== false;
        const status = success ? "✅ Success" : "❌ Failed";
        let message = `${status}: ${funcName}`;
        if (content.content && typeof content.content === "object" && content.content.agent_name) {
          message += ` → Transferred to: ${content.content.agent_name}`;
        }
        addMessage({
          type: innerType,
          content: message,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "tool_response") {
        const recipient = content.recipient || "Unknown";
        addMessage({
          type: innerType,
          content: `🔄 Tool Response: ${content.content}`,
          sender,
          role: "assistant",
        });
      } else if (innerType === "generate_code_execution_reply") {
        addMessage({
          type: innerType,
          content: "💻 Code execution completed",
          sender: "System",
          role: "system",
        });
      } else if (innerType === "group_chat_resume") {
        addMessage({
          type: innerType,
          content: "⏳ Resuming previous conversation state...",
          sender: "System",
          role: "system",
        });
      } else if (innerType === "info") {
        const infoContent = typeof content === "string" ? content : JSON.stringify(content);
        addMessage({
          type: innerType,
          content: `ℹ️ ${infoContent}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "termination_and_human_reply_no_input") {
        const msg = content.no_human_input_msg || "No human input provided";
        addMessage({
          type: innerType,
          content: `🚫 ${msg}`,
          sender: "System",
          role: "system",
        });
      } else if (innerType === "empty") {
        // Empty messages can be used for spacing or status indicators
        addMessage({
          type: innerType,
          content: content.content || " ",
          sender: "System",
          role: "system",
        });
      } else {
        addMessage({
          type: innerType,
          content: JSON.stringify(
            content.function_call || content.tool_calls || content,
            null,
            2
          ),
          sender,
          role: "assistant",
        });
      }
    }

    // Detect per-turn termination
    if (innerType === "termination") {
      setTaskStatus(`Turn ended: ${content?.termination_reason}`);
      if (taskIdRef.current && keycloak.token) updateWorkflowRunStatus(taskIdRef.current, "terminated", keycloak.token);
    }

    if (innerType === "run_completion") {
      if (taskIdRef.current && keycloak.token) updateWorkflowRunStatus(taskIdRef.current, "completed", keycloak.token);
    }
  };

  const handleRetry = async () => {
    setLoading(true);
    setError(null);
    setMessages([
      {
        role: "system",
        sender: "System",
        content: "Retrying workflow execution...",
        type: "text",
        timestamp: Date.now(),
      },
    ]);

    // Close existing WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Reset state
    setKnownSenders(new Set());
    setCurrentUser("You");
    setWaitingForInput(false);
    setCurrentRequestId(null);

    // Retry the task creation
    if (!authenticated || !keycloak?.token) {
      setError("You must be logged in to run a workflow.");
      setTaskStatus("Authentication required");
      setLoading(false);
      return;
    }
    try {
      const newToken = keycloak.token;
      const task = await createTask(newToken, workflowUrl);
      setTaskId(task.id);
      setTaskStatus(`New task created with ID: ${task.id}`);

      const wsUrl = `${webSocketServer}${task.id}`;
      connectWebSocket(wsUrl, newToken);

      setLoading(false);
    } catch (err) {
      console.error("Error retrying task:", err);
      setError("Failed to retry task: " + (err.message || "Unknown error"));

      addMessage({
        role: "system",
        sender: "System",
        content: `Error retrying: ${err.message || "Unknown error"}`,
        type: "error",
      });

      setLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!waitingForInput || !currentRequestId) return;

    const message = userInput.trim();

    // Add user message to chat
    addMessage({
      role: "user",
      sender: "You",
      content: message,
      type: "text",
    });

    // Send to WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "input_response",
          task_id: taskId,
          request_id: currentRequestId,
          timestamp: Date.now(),
          data: message,
        })
      );
    }

    // Reset input state
    setUserInput("");
    setWaitingForInput(false);
    setCurrentRequestId(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {renderParamsModal()}
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h2>Run Workflow</h2>
            {taskId && (
              <span className={styles.taskIdLabel}>Task ID: {taskId}</span>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className={styles.modalBody}>
            {/* Status bar */}
            <div className={styles.statusBar}>
              <div
                className={`${styles.statusIndicator} ${styles[taskStatus.replace(/\./g, "\\.")] || ""}`}
              ></div>
              <div className={styles.statusText}>
                <span>Status: {taskStatus}</span>
                {(taskStatus.includes("Loading...") || !taskStatus) && (
                  <span className={styles.loadingSpinner}></span>
                )}
              </div>
              {error && (
                <button className={styles.retryButton} onClick={handleRetry}>
                  <FontAwesomeIcon icon={faSync} /> Retry
                </button>
              )}
            </div>

            {/* Chat messages area */}
            <div className={styles.chatContainer} ref={chatBoxRef}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`${styles.messageContainer} ${styles[msg.role + "Container"]}`}
                >
                  <div
                    className={styles.avatarWrapper}
                    style={{
                      backgroundColor:
                        msg.role === "user"
                          ? USER_COLOR
                          : msg.role === "system"
                            ? SYSTEM_COLOR
                            : getColorForParticipant(msg.sender),
                    }}
                  >
                    <img
                      src={
                        msg.role === "user"
                          ? `${IMG_DIR}/user.webp`
                          : msg.role === "system"
                            ? `${IMG_DIR}/system.webp`
                            : `${IMG_DIR}/ai.webp`
                      }
                      alt={msg.sender}
                      className={styles.avatarImage}
                      onError={(e) => {
                        // Fallback if images don't load
                        e.target.style.display = "none";
                        e.target.parentNode.innerText =
                          msg.role === "user"
                            ? "U"
                            : msg.role === "system"
                              ? "S"
                              : "A";
                      }}
                    />
                  </div>
                  <div
                    className={`${styles.message} ${styles[msg.role + "Message"]}`}
                  >
                    <div className={styles.messageSender}>
                      {msg.role === "system" && (
                        <span className={styles.systemIcon}>⚙️</span>
                      )}
                      {msg.sender}
                    </div>
                    <div className={styles.messageContent}>
                      {msg.type !== "text" && (
                        <span className={styles.messageType}>[{msg.type}]</span>
                      )}
                      <span
                        dangerouslySetInnerHTML={{
                          __html: escapeHtml(msg.content),
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input area */}
            <div
              className={`${styles.inputContainer} ${!waitingForInput ? styles.hidden : ""}`}
            >
              <input
                type="text"
                className={styles.userInput}
                placeholder="Type your input here..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                disabled={!waitingForInput}
              />
              <button
                className={styles.sendButton}
                onClick={handleSendMessage}
                disabled={!waitingForInput}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RunWorkflowModal;
